/**
 * Bresenham line interpolation over integer cell coordinates.
 * Used by the UI layer to turn pointer movement into a continuous stroke.
 * Pure function: no DOM, no state.
 */

export interface Cell {
  x: number;
  y: number;
}

/**
 * All cells on the line from `from` to `to` (inclusive of both endpoints),
 * in drawing order from `from` to `to`. Same-point input yields one cell.
 * Non-integer inputs throw: the UI is expected to convert pointer
 * coordinates to integer cells first.
 */
export function lineCells(from: Cell, to: Cell): Cell[] {
  const x0 = assertInt(from.x, "from.x");
  const y0 = assertInt(from.y, "from.y");
  const x1 = assertInt(to.x, "to.x");
  const y1 = assertInt(to.y, "to.y");

  const cells: Cell[] = [];
  let x = x0;
  let y = y0;
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const stepX = x0 < x1 ? 1 : -1;
  const stepY = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  const count = 1 + dx + Math.abs(-dy) + 1; // upper bound; we break on arrival

  for (let i = 0; i < count; i++) {
    cells.push({ x, y });
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += stepX;
    }
    if (e2 <= dx) {
      err += dx;
      y += stepY;
    }
  }
  return cells;
}

function assertInt(value: number, name: string): number {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new Error(`lineCells: ${name} muss eine ganze Zahl sein (erhalten: ${String(value)}).`);
  }
  return value;
}
